/**
 * Firestore Path Service
 * 
 * This service provides helper methods to generate Firestore paths.
 * Since each project now has its own Firebase project, data is stored at the root level.
 * 
 * Usage:
 *   import { FirestorePathService } from './services/FirestorePathService';
 *   
 *   // Get collection reference
 *   const productsRef = FirestorePathService.getCollectionRef(db, 'products');
 *   
 *   // Get document reference
 *   const productRef = FirestorePathService.getDocRef(db, 'products', 'product123');
 */

import { Firestore, collection, doc, CollectionReference, DocumentReference } from 'firebase/firestore';

export class FirestorePathService {
  /**
   * Get the full path for a collection (at root level)
   */
  public static getCollectionPath(collectionName: string): string {
    return collectionName;
  }

  /**
   * Get the full path for a document: {collectionName}/{docId}
   */
  public static getDocumentPath(collectionName: string, docId: string): string {
    return `${collectionName}/${docId}`;
  }

  /**
   * Get a Firestore collection reference (at root level)
   * 
   * @param db - Firestore instance
   * @param collectionName - Name of the collection (e.g., 'products', 'videos')
   * @returns CollectionReference
   * 
   * @example
   * const productsRef = FirestorePathService.getCollectionRef(db, 'products');
   * const snapshot = await getDocs(productsRef);
   */
  public static getCollectionRef(db: Firestore, collectionName: string): CollectionReference {
    return collection(db, collectionName);
  }

  /**
   * Get a Firestore document reference (at root level)
   * 
   * @param db - Firestore instance
   * @param collectionName - Name of the collection
   * @param docId - Document ID
   * @returns DocumentReference
   * 
   * @example
   * const productRef = FirestorePathService.getDocRef(db, 'products', 'product123');
   * const docSnap = await getDoc(productRef);
   */
  public static getDocRef(db: Firestore, collectionName: string, docId: string): DocumentReference {
    return doc(db, collectionName, docId);
  }

  /**
   * Validate that a collection name is allowed
   */
  public static validateCollectionName(collectionName: string): boolean {
    const allowedCollections = [
      'admin',
      'catalogImages',
      'catalogPdf',
      'horario',
      'pricings',
      'products',
      'settings',
      'videos'
    ];
    
    return allowedCollections.includes(collectionName);
  }

  /**
   * Get collection reference with validation
   * Throws error if collection name is not allowed
   */
  public static getValidatedCollectionRef(db: Firestore, collectionName: string): CollectionReference {
    if (!this.validateCollectionName(collectionName)) {
      throw new Error(`Collection '${collectionName}' is not configured for access`);
    }
    return this.getCollectionRef(db, collectionName);
  }

  /**
   * Get document reference with validation
   * Throws error if collection name is not allowed
   */
  public static getValidatedDocRef(db: Firestore, collectionName: string, docId: string): DocumentReference {
    if (!this.validateCollectionName(collectionName)) {
      throw new Error(`Collection '${collectionName}' is not configured for access`);
    }
    return this.getDocRef(db, collectionName, docId);
  }

  /**
   * Debug helper - log current configuration
   */
  public static logConfiguration(): void {
    console.log('🔧 Firestore Path Service Configuration:');
    console.log(`   Example collection path: ${this.getCollectionPath('products')}`);
  }
}

export default FirestorePathService;
